import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FolderKanban, 
  Plus, 
  Clock, 
  CheckCircle2, 
  PauseCircle, 
  Circle,
  ArrowRight,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const [stats, setStats] = useState({
    total: 0,
    not_started: 0,
    in_progress: 0,
    on_hold: 0,
    completed: 0
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/projects/stats`),
        axios.get(`${API}/projects`)
      ]);
      setStats(statsRes.data);
      setRecentProjects(projectsRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      not_started: 'bg-slate-100 text-slate-700',
      in_progress: 'bg-blue-100 text-blue-700',
      on_hold: 'bg-amber-100 text-amber-700',
      completed: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      not_started: 'Not Started',
      in_progress: 'In Progress',
      on_hold: 'On Hold',
      completed: 'Completed'
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const statCards = [
    { label: 'Total Projects', value: stats.total, icon: FolderKanban, color: 'text-primary' },
    { label: 'Not Started', value: stats.not_started, icon: Circle, color: 'text-slate-500' },
    { label: 'In Progress', value: stats.in_progress, icon: Clock, color: 'text-blue-500' },
    { label: 'On Hold', value: stats.on_hold, icon: PauseCircle, color: 'text-amber-500' },
    { label: 'Completed', value: stats.completed, icon: CheckCircle2, color: 'text-emerald-500' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Overview of your project operations</p>
        </div>
        <Link to="/projects/new">
          <Button className="gap-2" data-testid="new-project-button">
            <Plus className="w-4 h-4" />
            New Project
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow" data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <p className="mt-4 text-3xl font-bold font-heading">{stat.value}</p>
              <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Projects */}
      <Card data-testid="recent-projects-card">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="font-heading text-xl">Recent Projects</CardTitle>
          <Link to="/projects">
            <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground hover:text-foreground">
              View all
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentProjects.length === 0 ? (
            <div className="text-center py-12">
              <FolderKanban className="w-12 h-12 mx-auto text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">No projects yet</p>
              <Link to="/projects/new">
                <Button variant="outline" className="mt-4 gap-2">
                  <Plus className="w-4 h-4" />
                  Create your first project
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentProjects.map((project) => (
                <Link 
                  key={project.id} 
                  to={`/projects/${project.id}`}
                  className="block"
                >
                  <div className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors" data-testid={`project-item-${project.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                          {project.project_number}
                        </span>
                        <h3 className="font-medium truncate">{project.project_name}</h3>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {project.location}
                        </span>
                        <span>{project.coordinator}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(project.status)}
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/projects?status=in_progress">
          <Card className="hover:shadow-md transition-shadow cursor-pointer group">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="font-heading font-semibold">Active Projects</h3>
                <p className="text-sm text-muted-foreground mt-1">View projects in progress</p>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/locations">
          <Card className="hover:shadow-md transition-shadow cursor-pointer group">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="font-heading font-semibold">Completed by Location</h3>
                <p className="text-sm text-muted-foreground mt-1">Browse completed projects by location</p>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}
